import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Alert, AlertTitle, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, LinearProgress, Tooltip,
  alpha, useTheme, IconButton, Collapse,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

// Gate descriptions and recommendations
const GATE_INFO = {
  confidence_threshold: { label: 'Confidence Threshold', desc: 'Signal confidence below required threshold', action: 'Lower threshold or wait for stronger signals' },
  volume_reject: { label: 'Volume Gate', desc: 'Insufficient trading volume (dry market)', action: 'Wait for volume; or disable volume gate (risky)' },
  sr_location: { label: 'S/R Location', desc: 'Entry too close to support/resistance', action: 'Normal — prevents buying resistance. Keep enabled.' },
  momentum_gate: { label: 'Momentum Gate', desc: 'Candle/EMA direction not confirming signal', action: 'Wait for confirmation; or loosen in trending markets' },
  entry_timing: { label: 'Entry Timing', desc: 'Price too close to recent 15m high/low', action: 'Widen timing threshold in schedule settings' },
  trend_30m: { label: '30m Trend Filter', desc: 'Signal against 30-minute trend direction', action: 'Disable for scalp mode (already off in Mode 1)' },
  strict_entry_wait: { label: 'Strict Entry', desc: 'Waiting for pullback or additional confirmation', action: 'Signal stored as pending — will retry next cycle' },
  reversal_gate: { label: 'Reversal Gate', desc: 'High reversal risk detected', action: 'Reduce leverage or wait for trend confirmation' },
  scalp_gate: { label: 'Scalp Gate', desc: 'Scalp-specific momentum/quality check failed', action: 'Normal for Mode 1. Adjust profile if too strict.' },
  leverage_sell_block: { label: 'Leverage Sell Block', desc: 'Sells blocked at high leverage', action: 'Normal safety — no shorts at 20×' },
  leverage_sell_weak: { label: 'Leverage Sell Weak', desc: 'Sell lacks strong bearish evidence', action: 'Normal — sell needs strong confirmation at 10×+' },
  side_policy: { label: 'Side Policy', desc: 'Direction blocked by buy-only/sell-only policy', action: 'Change side policy in settings if needed' },
  ai_extreme_risk: { label: 'AI Risk Block', desc: 'AI chart analysis detected extreme risk', action: 'Normal safety check — keep enabled' },
  correlation_reject: { label: 'Correlation Reject', desc: 'Cross-asset correlation flagged danger', action: 'Normal — prevents correlated trades' },
  '5m_confirm': { label: '5m Confirmation', desc: '5-minute trend not confirming entry direction', action: 'Signal stored as pending — retries when confirmed' },
  duplicate: { label: 'Duplicate Guard', desc: 'Symbol already opened this cycle', action: 'Normal — prevents double entries' },
  price_fetch: { label: 'Price Fetch Error', desc: 'Could not fetch real-time price', action: 'Temporary — exchange API issue' },
  loadout_veto: { label: 'Loadout Veto', desc: 'Market regime blocks all entries', action: 'Regime-based protection (chop/spike). Wait it out.' },
  htf_sell_veto: { label: 'HTF Sell Veto', desc: 'Sells blocked by current market regime', action: 'Normal in ranging/bullish markets' },
  pro_mode_cap: { label: 'Pro Mode Cap', desc: 'Signal count capped by Pro Mode limits', action: 'Normal — Pro mode limits per-scan entries' },
  skip_open_symbols: { label: 'Open Symbol Skip', desc: 'Symbol already has an open position', action: 'Normal — prevents duplicate symbols' },
  max_concurrent: { label: 'Max Concurrent', desc: 'All position slots are full', action: 'Increase max concurrent or wait for exits' },
  max_per_symbol: { label: 'Max Per Symbol', desc: 'Too many positions on same symbol', action: 'Normal limit — prevents concentration' },
};

// ============================================================================
// FUNNEL VISUAL
// ============================================================================
function GateFunnel({ rejectionData }) {
  const theme = useTheme();

  if (!rejectionData || rejectionData.error) {
    return (
      <Alert severity="info" variant="outlined">
        <Typography variant="caption">No scan data available yet. Wait for the next scan cycle.</Typography>
      </Alert>
    );
  }

  const stages = [
    { label: 'Signals Found', value: rejectionData.totalSignals || 0, color: theme.palette.primary.main },
    { label: 'Above Threshold', value: rejectionData.qualified || 0, color: theme.palette.info.main },
    { label: 'Gate Failures', value: (rejectionData.rejections || []).length, color: theme.palette.warning.main, isLoss: true },
    { label: 'Entered', value: rejectionData.entries || 0, color: theme.palette.success.main },
  ];

  const maxVal = Math.max(...stages.filter(s => !s.isLoss).map(s => s.value), 1);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
        Signal Funnel (Last Scan)
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {stages.map((stage, i) => {
          const width = stage.isLoss ? (stage.value / maxVal) * 100 : (stage.value / maxVal) * 100;
          return (
            <Box key={i}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography variant="caption" fontWeight={500}>{stage.label}</Typography>
                <Typography variant="caption" fontWeight="bold" color={stage.color}>{stage.value}</Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 20, bgcolor: alpha(theme.palette.text.disabled, 0.08), borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0, top: 0, bottom: 0,
                    width: `${Math.min(width, 100)}%`,
                    bgcolor: alpha(stage.color, stage.isLoss ? 0.3 : 0.5),
                    borderRadius: 1,
                    transition: 'width 0.5s ease',
                    minWidth: stage.value > 0 ? 8 : 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Conversion rate */}
      {rejectionData.totalSignals > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Conversion: {((rejectionData.entries / rejectionData.totalSignals) * 100).toFixed(1)}% of signals → entries
        </Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// GATE BLOCK-RATE MATRIX
// ============================================================================
function GateMatrix({ rejectionData, scheduleData }) {
  const theme = useTheme();

  const gateStats = useMemo(() => {
    if (!rejectionData || !rejectionData.rejections) return [];

    const rejections = rejectionData.rejections || [];
    const gateCounts = {};
    rejections.forEach(r => {
      if (!gateCounts[r.gate]) {
        gateCounts[r.gate] = { count: 0, samples: [] };
      }
      gateCounts[r.gate].count++;
      if (gateCounts[r.gate].samples.length < 3) {
        gateCounts[r.gate].samples.push(r);
      }
    });

    const total = rejections.length || 1;
    return Object.entries(gateCounts)
      .map(([gate, data]) => {
        const info = GATE_INFO[gate] || { label: gate, desc: 'Unknown gate', action: 'Check configuration' };
        return {
          gate,
          label: info.label,
          description: info.desc,
          action: info.action,
          count: data.count,
          percent: ((data.count / total) * 100).toFixed(0),
          samples: data.samples,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [rejectionData]);

  if (gateStats.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Gate Block Rates</Typography>
        <Typography variant="caption" color="text.secondary">No gate rejections in last scan.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
        Gate Block Rates (sorted by frequency)
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Gate</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Blocked</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Typical Reason</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Recommendation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gateStats.map(g => (
              <GateRow key={g.gate} gate={g} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function GateRow({ gate }) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const severityColor = gate.count >= 5 ? 'error.main' : gate.count >= 2 ? 'warning.main' : 'text.secondary';

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BlockIcon sx={{ fontSize: 14, color: severityColor }} />
            <Typography variant="caption" fontWeight={500}>{gate.label}</Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={`${gate.count} (${gate.percent}%)`}
            size="small"
            sx={{ fontSize: '0.65rem', height: 22, bgcolor: alpha(theme.palette.warning.main, 0.1) }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">{gate.description}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LightbulbIcon sx={{ fontSize: 12, color: 'info.main' }} />
            <Typography variant="caption" color="info.main">{gate.action}</Typography>
          </Box>
        </TableCell>
      </TableRow>
      {expanded && gate.samples.length > 0 && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 1, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Examples:</Typography>
            {gate.samples.map((s, i) => (
              <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ ml: 1 }}>
                • {s.symbol}: {s.reason?.substring(0, 100)}
              </Typography>
            ))}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ============================================================================
// FILTER STEPS
// ============================================================================
function FilterStepsSummary({ rejectionData }) {
  const theme = useTheme();
  const filterSteps = rejectionData?.filterSteps || [];

  if (filterSteps.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        Pre-Gate Filter Steps
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        These filters reduce signal count before individual gate checks:
      </Typography>
      {filterSteps.map((fs, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <FilterAltIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={500} sx={{ minWidth: 160 }}>{fs.step}</Typography>
          <Typography variant="caption">{fs.before} → {fs.after}</Typography>
          <Chip label={`-${fs.dropped}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
        </Box>
      ))}
    </Paper>
  );
}

// ============================================================================
// SILENT DROPS WARNING
// ============================================================================
function SilentDropsWarning({ rejectionData }) {
  if (!rejectionData) return null;

  const total = rejectionData.totalSignals || 0;
  const qualified = rejectionData.qualified || 0;
  const entered = rejectionData.entries || 0;
  const rejected = (rejectionData.rejections || []).length;

  // If qualified - entered - rejected > 0, signals disappeared without explanation
  const unaccounted = qualified - entered - rejected;
  if (unaccounted <= 0) return null;

  return (
    <Alert severity="error" variant="outlined" icon={<WarningAmberIcon />}>
      <AlertTitle sx={{ fontSize: '0.8rem' }}>Pipeline Drop Detected</AlertTitle>
      <Typography variant="caption">
        {unaccounted} signal(s) qualified but have no rejection log and didn't result in entries.
        This may indicate a code path that silently drops signals. Check worker logs for details.
      </Typography>
    </Alert>
  );
}

// ============================================================================
// MAIN TAB
// ============================================================================
export default function SignalsGatesTab({ strategyId, rejectionData, scheduleData, modeInfo, onRefresh }) {
  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left: Funnel + Silent Drops */}
        <Grid item xs={12} md={5}>
          <GateFunnel rejectionData={rejectionData} />
          <Box sx={{ mt: 2 }}>
            <SilentDropsWarning rejectionData={rejectionData} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <FilterStepsSummary rejectionData={rejectionData} />
          </Box>
        </Grid>

        {/* Right: Gate Matrix */}
        <Grid item xs={12} md={7}>
          <GateMatrix rejectionData={rejectionData} scheduleData={scheduleData} />
        </Grid>
      </Grid>
    </Box>
  );
}
